import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { PlanLockedCard } from '@/components/ui';
import { api } from '@/lib/api';
import { auditLogApi, AuditLogRow } from '@/lib/auditLog';
import { usePlan } from '@/hooks/usePlan';
import { formatLocaleDate } from '@/lib/date';

interface Company {
  id: string;
  name: string;
}

function csvEscape(value: unknown): string {
  if (value == null) return '';
  const s = typeof value === 'string' ? value : JSON.stringify(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function buildCsv(rows: AuditLogRow[]): string {
  const header = [
    'action',
    'target_kind',
    'target_id',
    'user_id',
    'occurred_at',
    'payload',
  ];
  const lines = [header.join(',')];
  for (const r of rows) {
    lines.push(
      [
        csvEscape(r.action),
        csvEscape(r.target_kind),
        csvEscape(r.target_id),
        csvEscape(r.user_id),
        csvEscape(r.occurred_at),
        csvEscape(r.payload ?? {}),
      ].join(','),
    );
  }
  return lines.join('\n');
}

export function AuditLogPage() {
  const { t, i18n } = useTranslation();
  const [filter, setFilter] = useState('');
  const plan = usePlan();
  const planAllows = plan.meets('global');

  const companies = useQuery<Company[]>({
    queryKey: ['companies', 'mine'],
    queryFn: async () => (await api.get<Company[]>('/api/companies')).data,
  });
  const companyId = companies.data?.[0]?.id;

  const list = useQuery({
    queryKey: ['audit-log', companyId],
    queryFn: () => auditLogApi.list(companyId!, 200),
    enabled: !!companyId && planAllows,
  });

  const rows: AuditLogRow[] = useMemo(
    () =>
      (list.data ?? []).filter(
        (r) =>
          !filter ||
          r.action.toLowerCase().includes(filter.toLowerCase()) ||
          (r.target_kind || '').toLowerCase().includes(filter.toLowerCase()),
      ),
    [list.data, filter],
  );

  const handleExport = () => {
    const csv = buildCsv(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 10);
    a.download = `${t('audit_log.export_filename')}-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!plan.isLoading && !planAllows) {
    return (
      <AppShell titleKey="audit_log.title">
        <PlanLockedCard
          required="global"
          description={t('plan.locked_audit_log_body')}
        />
      </AppShell>
    );
  }

  return (
    <AppShell titleKey="audit_log.title">
      <p className="text-sm text-ink-600 max-w-2xl mb-4">{t('audit_log.intro')}</p>

      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <input
          type="search"
          placeholder={t('audit_log.filter_placeholder')}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-72 rounded-md border border-ink-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
        />
        <button
          type="button"
          onClick={handleExport}
          disabled={rows.length === 0}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-ink-300 text-ink-700 hover:bg-ink-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download className="h-3.5 w-3.5" />
          {t('audit_log.export_csv')}
        </button>
      </div>

      {list.isLoading && <p className="text-sm text-ink-500">{t('common.loading')}</p>}

      {!list.isLoading && rows.length === 0 && (
        <div className="rounded-2xl border border-dashed border-ink-300 p-10 text-center text-ink-500 max-w-3xl">
          <p className="text-sm">{t('audit_log.empty_title')}</p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm bg-white rounded-2xl border border-ink-200">
            <thead className="text-left text-ink-500 border-b border-ink-200">
              <tr>
                <th className="py-2.5 px-3 font-medium">{t('audit_log.col_action')}</th>
                <th className="py-2.5 px-3 font-medium">{t('audit_log.col_target')}</th>
                <th className="py-2.5 px-3 font-medium">{t('audit_log.col_user')}</th>
                <th className="py-2.5 px-3 font-medium">{t('audit_log.col_time')}</th>
                <th className="py-2.5 px-3 font-medium">{t('audit_log.col_payload')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-ink-100">
                  <td className="py-2.5 px-3 font-medium text-ink-900 num">{r.action}</td>
                  <td className="py-2.5 px-3 num text-ink-700">
                    {r.target_kind || '—'}
                    {r.target_id && (
                      <span className="text-ink-400">#{r.target_id.slice(0, 8)}</span>
                    )}
                  </td>
                  <td className="py-2.5 px-3 num text-ink-500 text-xs">
                    {r.user_id ? r.user_id.slice(0, 8) : '—'}
                  </td>
                  <td className="py-2.5 px-3 num text-ink-500 text-xs">
                    {formatLocaleDate(r.occurred_at, i18n.language, { withTime: true })}
                  </td>
                  <td className="py-2.5 px-3 text-ink-500 text-xs max-w-xs truncate">
                    {Object.keys(r.payload || {}).length > 0
                      ? JSON.stringify(r.payload).slice(0, 80)
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AppShell>
  );
}
